import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Brain, Stethoscope, Check, Star, Zap, Crown, Sun, Moon, Flower2, Sparkles
} from 'lucide-react';
import FeatureCategories from '../components/FeatureCategories';

export const Landing = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Get theme icon config
  const getThemeConfig = () => {
    switch (theme) {
      case 'dark':
        return { icon: Moon, color: '#FFA500', bg: 'linear-gradient(135deg, #2A3441 0%, #1E2530 100%)', shadow: '0 4px 12px rgba(0, 0, 0, 0.4)' };
      case 'cyberpunk':
        return { icon: Zap, color: '#00FFFF', bg: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)', shadow: '0 0 20px rgba(255, 0, 255, 0.5)' };
      case 'floral':
        return { icon: Flower2, color: '#FFFFFF', bg: 'linear-gradient(135deg, #996515 0%, #7A5410 100%)', shadow: '0 4px 15px rgba(153, 101, 21, 0.5)' };
      case 'anime':
        return { icon: Sparkles, color: '#FFFFFF', bg: 'linear-gradient(135deg, #A03028 0%, #8B2520 100%)', shadow: '0 4px 15px rgba(160, 48, 40, 0.5)' };
      default:
        return { icon: Sun, color: '#FFFFFF', bg: 'linear-gradient(135deg, #FFE066 0%, #FFA500 100%)', shadow: '0 4px 12px rgba(255, 165, 0, 0.4)' };
    }
  };

  const themeConfig = getThemeConfig();
  const ThemeIcon = themeConfig.icon;

  const pricingPlans = [
    { id: 'weekly', name: 'Weekly', price: 6, period: 'week', icon: Zap },
    { id: 'monthly', name: 'Monthly', price: 12, period: 'month', icon: Star, popular: false },
    { id: 'quarterly', name: 'Quarterly', price: 30, period: '3 months', icon: Crown, popular: true },
    { id: 'yearly', name: 'Yearly', price: 100, period: 'year', icon: Crown },
  ];

  return (
    <div className="min-h-screen">
      {/* Theme Toggle for Landing Page */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className="p-3 rounded-full transition-all duration-300 shadow-lg"
          style={{
            background: themeConfig.bg,
            boxShadow: themeConfig.shadow
          }}
          data-testid="landing-theme-toggle"
          title="Switch Theme"
        >
          <ThemeIcon className="w-5 h-5" style={{ color: themeConfig.color }} />
        </button>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                Your Mental Health,
                <br />
                <span style={{ color: 'var(--text-dark)' }}>Supported & Heard</span>
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-medium)' }}>
                Track your mood, manage medications, connect with peers and professionals, access AI support, and find emergency resources—all in one safe, supportive space.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E' }}>
                  ✓ 18+ Features
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6' }}>
                  ✓ Video Consultations
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'rgba(168, 85, 247, 0.15)', color: '#A855F7' }}>
                  ✓ AI Companion
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/auth?mode=register')}
                  className="px-8 py-4 rounded-xl btn-primary-cta hover:scale-105 transition-transform text-lg font-semibold"
                  data-testid="hero-get-started-button"
                >
                  Get Started Free
                </button>
                <button
                  onClick={() => navigate('/auth?mode=login')}
                  className="px-8 py-4 rounded-xl font-semibold text-lg transition-all btn-secondary"
                  data-testid="hero-login-button"
                >
                  Sign In
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758273240360-76b908e7582a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx0aGVyYXB5JTIwc2Vzc2lvbiUyMG1lbnRhbCUyMGhlYWx0aCUyMGNvdW5zZWxpbmclMjBzdXBwb3J0fGVufDB8fHx8MTc2ODg0NjcyM3ww&ixlib=rb-4.1.0&q=85"
                alt="Therapy session mental health support"
                className="rounded-2xl shadow-warm-lg w-full h-auto max-h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="feature-card text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sunrise-dark-orange)' }}>18+</div>
              <div className="text-sm" style={{ color: 'var(--text-medium)' }}>Features</div>
            </div>
            <div className="feature-card text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sunrise-dark-orange)' }}>24/7</div>
              <div className="text-sm" style={{ color: 'var(--text-medium)' }}>AI Support</div>
            </div>
            <div className="feature-card text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sunrise-dark-orange)' }}>100%</div>
              <div className="text-sm" style={{ color: 'var(--text-medium)' }}>Encrypted</div>
            </div>
            <div className="feature-card text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sunrise-dark-orange)' }}>Free</div>
              <div className="text-sm" style={{ color: 'var(--text-medium)' }}>Basic Plan</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section - Expandable Categories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="features-section">
        <FeatureCategories />
      </section>

      {/* Doctor Portal Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: 'rgba(6, 182, 212, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="px-3 py-1 rounded-full text-sm font-medium mb-4 inline-block" style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06B6D4' }}>
                For Healthcare Providers
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                Doctor's Portal
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--text-medium)' }}>
                A dedicated dashboard for mental health professionals to monitor and support their patients effectively.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
                  <div>
                    <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Patient Connection System</span>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Generate secure codes for patients to connect with you</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
                  <div>
                    <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Mood & Wellness Monitoring</span>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>View patient mood trends and wellness data in real-time</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
                  <div>
                    <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Secure Vault Access</span>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>View patient's private vault entries with their consent</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
                  <div>
                    <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Video Consultations</span>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Conduct secure video sessions with connected patients</p>
                  </div>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth?mode=register')}
                className="mt-8 px-6 py-3 rounded-xl font-semibold text-white"
                style={{ background: '#06B6D4' }}
              >
                Register as Doctor
              </button>
            </div>
            <div className="feature-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <Stethoscope className="w-10 h-10" style={{ color: '#06B6D4' }} />
                <div>
                  <h3 className="text-xl font-bold" style={{ color: 'var(--text-black)' }}>Dr. Sarah Johnson</h3>
                  <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Psychiatrist • City Hospital</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 rounded-xl" style={{ background: 'rgba(6, 182, 212, 0.1)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-black)' }}>Connected Patients</p>
                  <p className="text-2xl font-bold" style={{ color: '#06B6D4' }}>24</p>
                </div>
                <div className="p-4 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-black)' }}>Appointments Today</p>
                  <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>5</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white" style={{ background: 'var(--sunrise-dark-orange)' }}>1</div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-black)' }}>Create Account</h3>
              <p style={{ color: 'var(--text-medium)' }}>Sign up as an individual user or healthcare provider. Email verification ensures security.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white" style={{ background: 'var(--sunrise-dark-orange)' }}>2</div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-black)' }}>Track & Connect</h3>
              <p style={{ color: 'var(--text-medium)' }}>Log your mood daily, connect with your doctor, and explore the features that support you.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white" style={{ background: 'var(--sunrise-dark-orange)' }}>3</div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-black)' }}>Grow & Heal</h3>
              <p style={{ color: 'var(--text-medium)' }}>Use insights, talk to peers, get professional help, and take control of your mental wellness.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="pricing-section" id="pricing">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            Plans & Pricing
          </h2>
          <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: 'var(--text-medium)' }}>
            Start free with essential features. Upgrade to Pro for the complete mental wellness experience.
          </p>
          
          {/* Free vs Pro Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
            <div className="feature-card">
              <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-black)' }}>Free Plan</h3>
              <p className="text-2xl font-bold mb-4" style={{ color: 'var(--sunrise-dark-orange)' }}>$0 <span className="text-sm font-normal" style={{ color: 'var(--text-light)' }}>forever</span></p>
              <ul className="space-y-3">
                {['Mood Journal', 'Find Professional Help', 'Wellness Store', 'Emergency Support', 'Content Library', 'Calendar Sync'].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-medium)' }}>
                    <Check className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/auth?mode=register')}
                className="w-full mt-6 py-3 rounded-xl font-semibold btn-secondary"
              >
                Get Started Free
              </button>
            </div>
            <div className="feature-card popular-plan">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold" style={{ color: 'var(--text-black)' }}>Pro Plan</h3>
                <span className="px-2 py-1 text-xs font-bold rounded-full" style={{ background: 'var(--sunrise-dark-orange)', color: 'white' }}>POPULAR</span>
              </div>
              <p className="text-2xl font-bold mb-4" style={{ color: 'var(--sunrise-dark-orange)' }}>$12 <span className="text-sm font-normal" style={{ color: 'var(--text-light)' }}>/month</span></p>
              <ul className="space-y-3">
                {['Everything in Free', 'MindPulse™ Wellness Alerts', 'AI Companion (Amigo)', 'Video Consultations', 'Private Mental Vault', 'Doctor-Ready Summary', 'Just Talk Peer Support', 'Mental Health Forums', 'Group Therapy Sessions', 'Quantum Manifestation'].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-medium)' }}>
                    <Check className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/auth?mode=register')}
                className="w-full mt-6 py-3 rounded-xl font-semibold btn-primary-cta"
              >
                Start Pro Trial
              </button>
            </div>
          </div>

          {/* Plan Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 pt-4" style={{ overflow: 'visible' }}>
            {pricingPlans.map((plan) => {
              const PlanIcon = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={`relative feature-card p-6 pt-8 flex flex-col ${
                    plan.popular ? 'popular-plan' : ''
                  }`}
                  style={{ overflow: 'visible' }}
                  data-testid={`landing-plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-white text-sm font-semibold rounded-full whitespace-nowrap z-10" style={{ background: 'var(--sunrise-dark-orange)' }}>
                      Best Value
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'rgba(255, 165, 0, 0.15)' }}>
                      <PlanIcon className="w-6 h-6" style={{ color: 'var(--sunrise-orange)' }} />
                    </div>
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1 mt-2">
                      <span className="text-3xl font-bold" style={{ color: 'var(--text-black)' }}>${plan.price}</span>
                      <span className="text-sm" style={{ color: 'var(--text-light)' }}>/{plan.period}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/auth?mode=register')}
                    className="w-full py-2.5 rounded-xl font-semibold text-sm btn-primary-cta"
                  >
                    Get Started
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center feature-card">
          <img 
            src="/logo-192.png" 
            alt="HearMeOut Logo" 
            className="h-16 mx-auto mb-6 object-contain"
          />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            Start Your Wellness Journey Today
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-medium)' }}>
            Join thousands of users who are taking control of their mental health. Your journey to wellness begins with a single step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/auth?mode=register')}
              className="px-8 py-4 rounded-xl btn-primary-cta hover:scale-105 transition-transform text-lg font-semibold"
            >
              Create Free Account
            </button>
            <Link
              to="/about"
              className="px-8 py-4 rounded-xl font-semibold text-lg btn-secondary"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
